// Models
import { Selector } from 'react-redux';
import { PaginationPayload } from 'app/shared/pagination/models';
import { RuleStatuses } from 'app/modules/rules/models';
import { CaseStatus } from 'app/modules/cases/models';
import { QueueAccessType } from 'app/modules/queues/models';
import { ElementType, ReactNode } from 'react';
import { Permission } from 'app/shared/utils/permissions';
import { ShortEntityResponse } from 'app/modules/entities/responses';

export enum SearchActionTypes {
  ENTITIES_SEARCH = '@@search/ENTITIES_SEARCH',
  ENTITIES_SEARCH_SUCCESS = '@@search/ENTITIES_SEARCH_SUCCESS',

  TEAMS_SEARCH = '@@search/TEAMS_SEARCH',
  TEAMS_SEARCH_SUCCESS = '@@search/TEAMS_SEARCH_SUCCESS',
  REMOVE_TEAM_FROM_SEARCH = '@@search/REMOVE_TEAM_FROM_SEARCH',

  RULES_SEARCH = '@@search/RULES_SEARCH',
  RULES_SEARCH_SUCCESS = '@@search/RULES_SEARCH_SUCCESS',

  ALERTS_SEARCH = '@@search/ALERTS_SEARCH',
  ALERTS_SEARCH_SUCCESS = '@@search/ALERTS_SEARCH_SUCCESS',

  EVENTS_SEARCH = '@@search/EVENTS_SEARCH',
  EVENTS_SEARCH_SUCCESS = '@@search/EVENTS_SEARCH_SUCCESS',
}

export enum SearchTypes {
  ACTION_EVENTS = 'actionEvents',
  ALERTS = 'alerts',
  CASES = 'cases',
  ENTITIES = 'entities',
  FLOWS = 'flows',
  INSTRUMENTS = 'instruments',
  RULES = 'rules',
  TEAMS = 'teams',
  TRANSACTIONS = 'events',
  NOTES = 'notes',
}

export interface TypeDropdownOption {
  key: string;
  value: string;
  text: string;
  // to add to the end of the search placeholder so we know what we are searching
  placeholder: string;
}

export interface SearchRequest {
  phrase: string;
  offset: number;
  limit: number;
  type?: string;
  status?: string;
  ids?: (number | string)[];
  external_ids?: string[];
  exclude_sync_rules?: boolean;
}

export interface CGDOSearchRequest {
  phrase: string;
  offset: number;
  limit: number;
  type?: string;
  status?: string;
  ids?: (number | string)[];
}

export interface EntitySearchRequest extends CGDOSearchRequest {
  external_ids?: string[];
}

export interface InstrumentSearchRequest extends CGDOSearchRequest {
  external_ids?: string[];
}

export interface PaginatedSearchRequest extends PaginationPayload {
  phrase: string;
}

export interface EntitySearchResponse {
  data: EntitiesSearchResult[];
}

export interface EntitiesSearchResult {
  id: number;
  external_id: string;
  type: ShortEntityResponse['entity_type'];
  email?: string[];
  first_name?: string | null;
  last_name?: string;
  middle_name?: string;
  user_name?: string;
  name?: string;
  name_readable?: string;
  custom_data?: string;
  unit21_id?: string | 'UNIT21_ID_NOT_FOUND';
}

export interface EntitiesSearchV2Result {
  id: number | string;
  external_id: string;
  type: ShortEntityResponse['entity_type'];
  email_addresses?: string[];
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  user_name?: string;
  name_readable?: string;
  unit21_id: string;
  business_name?: string;
}

interface EntitiesState {
  data: EntitiesSearchResult[];
}

export interface AlertsSearchResult {
  id: number;
  title: string;
  description?: string;
  external_id?: string;
  queue_id: number | null;
  queue_access_type?: `${QueueAccessType}`;
  // not using AlertStatus to avoid a bunch of typing changes elsewhere
  status: string;
}

interface AlertsState {
  data: AlertsSearchResult[];
}

export interface AlertsSearchResponse {
  data: AlertsSearchResult[];
}

export interface CasesSearchResult {
  id: number;
  title: string;
  description?: string;
  status: CaseStatus;
  queue_id: number | null;
  queue_access_type?: `${QueueAccessType}`;
}

export interface CasesSearchResponse {
  data: CasesSearchResult[];
}

export interface EventsSearchResult {
  id: number | string;
  external_id: string;
  unit21_id?: string | 'UNIT21_ID_NOT_FOUND';
}

export interface FlowsSearchResult {
  id: number;
  title: string;
  description: string;
  status: string;
}

export interface FlowsSearchResponse {
  count: number;
  data: FlowsSearchResult[];
}

interface EventsState {
  data: EventsSearchResult[];
}

interface ActionEventsState {
  data: EventsSearchResult[];
}

export interface EventsSearchResponse {
  data: EventsSearchResult[];
}

export interface InstrumentsSearchResponse {
  data: InstrumentsSearchResult[];
}

export interface InstrumentsSearchResult {
  id: number | string;
  instrument_type: string;
  name: string;
  source: string;
  status: string;
  external_id: string;
}
export interface RulesSearchResult {
  id: number;
  title: string;
  description: string;
  status: RuleStatuses;
  alert_queue_id?: number;
  alert_queue_title?: string;
  is_synchronous: boolean | null;
}

export interface RulesSearchResponse {
  count: number;
  data: RulesSearchResult[];
}

export interface TeamsSearchResult {
  id: number;
  name: string;
  description: string;
}

export interface TeamsSearchResponse {
  count: number;
  data: TeamsSearchResult[];
}

export type NoteOwnersType =
  | 'ALERT'
  | 'CASE'
  | 'SAR_FILING'
  | 'CTR_FILING'
  | 'RULE'
  | 'ENTITY'
  | 'INSTRUMENT';

export interface NotesSearchResult {
  id: number;
  note_owners_type: NoteOwnersType;
  note_owners_title: string;
  note_owners_unit21_id: string;
  highlight: string;
}

export interface NotesSearchResponse {
  count: number;
  data: NotesSearchResult[];
}

export interface TeamsState {
  count: number;
  data: TeamsSearchResult[];
}

export interface RulesState {
  count: number;
  data: RulesSearchResult[];
}

export interface SearchResultProps {
  description?: string;
  icon: ReactNode;
  label?: string;
  queueAccessType?: `${QueueAccessType}`;
  queueId?: number | null;
  title: string;
  url: string;
  value: string | number;
}

export interface ResourceDropdownItem {
  icon: ElementType;
  text: string;
  value: SearchTypes;
  type?: string;
  placeholder: string;
  formatSearchResult: (result: any) => SearchResultProps;
  resultsSelector?: Selector<RootState, any>;
  permissions: Permission[];
}

export interface SearchState {
  entities: EntitiesState;
  rules: RulesState;
  alerts: AlertsState;
  events: EventsState;
  teams: TeamsState;
}

export interface NewSearchState {
  actionEvents: ActionEventsState;

  // Loading
  actionEventsLoading: boolean;
}

export type SearchResponse =
  | AlertsSearchResponse
  | CasesSearchResponse
  | EntitySearchResponse
  | EventsSearchResponse
  | FlowsSearchResponse
  | InstrumentsSearchResponse
  | RulesSearchResponse
  | TeamsSearchResponse
  | NotesSearchResponse;

export type SearchResult =
  | AlertsSearchResult
  | CasesSearchResult
  | EntitiesSearchResult
  | EventsSearchResult
  | FlowsSearchResult
  | InstrumentsSearchResult
  | RulesSearchResult
  | TeamsSearchResult
  | NotesSearchResult;
